import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebookF } from "@fortawesome/free-brands-svg-icons";
import { useLanguage } from "../context/LanguageContext";
import { getInformation } from "../services/api";
import { Information } from "../types/types";
import { resolveMediaUrl } from "../utils/media";

const Footer: React.FC = () => {
  const { language } = useLanguage();
  const [information, setInformation] = useState<Information | null>(null);
  const [loading, setLoading] = useState(true);

  const isAr = language === "ar";

  useEffect(() => {
    fetchInformation();
  }, []);

  const fetchInformation = async () => {
    try {
      const response = await getInformation();
      if (response.data && response.data.length > 0) {
        setInformation(response.data[0]);
      }
    } catch (error) {
      console.error("Footer information error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-white text-gray-700 border-t">
      {/* TOP */}
      <div className="container mx-auto px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-6">
        {/* LOGO + TEXT */}
        <div dir={isAr ? "rtl" : "ltr"} className="flex items-center gap-4">
          {/* LOGO - Now correctly positioned in RTL */}
          <img
            src={
              information?.logo
                ? resolveMediaUrl(information.logo)
                : "https://via.placeholder.com/60x60"
            }
            alt="Ministry Logo"
            className="h-12 w-12 rounded-full object-cover border border-gray-300 shrink-0"
          />

          {/* TEXT */}
          <div className={`leading-tight ${isAr ? "text-right" : "text-left"}`}>
            <p className="text-xs font-semibold text-gray-600">
              {isAr
                ? "الجمهورية الإسلامية الموريتانية"
                : "République Islamique de Mauritanie"}
            </p>
            <h2 className="text-sm md:text-base font-bold text-gray-800 mt-1">
              {isAr
                ? "وزارة العقارات وأملاك الدولة والإصلاح العقاري"
                : "Ministère des Domaines et des Affaires Foncières et de la Réforme Foncière"}
            </h2>
          </div>
        </div>

        {/* LINKS */}
        <div
          className={`flex items-center gap-6 text-sm font-medium ${
            isAr ? "flex-row-reverse" : "flex-row"
          }`}
        >
          <a href="/" className="hover:text-[#ce1f26] transition">
            {isAr ? "الرئيسية" : "Accueil"}
          </a>

          <a href="/contact" className="hover:text-[#ce1f26] transition">
            {isAr ? "اتصل بنا" : "Contact"}
          </a>

          <a
            href="https://web.facebook.com/profile.php?id=100092540392286"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            className="w-9 h-9 rounded-full border border-gray-300 text-gray-600 flex items-center justify-center hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition"
          >
            <FontAwesomeIcon icon={faFacebookF} />
          </a>
        </div>
      </div>

      {/* COPYRIGHT */}
      <div className="border-t border-gray-200 text-center text-sm text-gray-500 py-4 px-6 space-y-1">
        <div>
          © {new Date().getFullYear()}{" "}
          {isAr
            ? "وزارة العقارات وأملاك الدولة والإصلاح العقاري - الجمهورية الإسلامية الموريتانية"
            : "Ministère des Domaines et des Affaires Foncières et de la Réforme Foncière – République Islamique de Mauritanie"}
        </div>
        <div>
          {isAr
            ? "إصدار المشروع v1.0 - 30-04-2026"
            : "Version du projet v1.0 - 30-04-2026"}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
